import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { supplierService } from '../services/supplierService';
import { useLanguage } from '../contexts/LanguageContext';
import { getCurrencySymbol } from '../utils/currency';
import EnteteEcran from '../components/commun/EnteteEcran';
import BarreFiltres from '../components/commun/BarreFiltres';
import DialogueConfirmation from '../components/commun/DialogueConfirmation';
import PiedTableau from '../components/commun/PiedTableau';
import DialogueDetailFournisseur from '../components/fournisseurs/DialogueDetailFournisseur';
import { useNavigate, useParams } from 'react-router-dom';
import { useStock } from '../contexts/StockContext';
import { canDelete, DELETABLE } from '../utils/permissions';

const schema = yup.object().shape({
  company_name: yup.string().required('Le nom de la société est requis'),
  email: yup.string().email('Email invalide'),
  phone: yup.string(),
  contact_name: yup.string(),
  address: yup.string(),
  communication_language: yup.string(),
  currency: yup.string(),
});

const Suppliers = ({ showSnackbar, user }) => {
  const { t } = useLanguage();
  const { currentStock, triggerRefresh } = useStock();
  const { id: fournisseurDetailleId } = useParams();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [parPage, setParPage] = useState(20);

  /**
   * L'annonce avant un geste irréversible — plus de dialogue natif.
   *
   * 🔴 *Un dialogue natif se valide sans qu'on lise l'annonce* : il ne montre
   * rien, il bloque le fil, et il ne suit ni le thème ni la langue. Le
   * propriétaire a annulé une commande par accident sur exactement ce motif.
   */
  const [aConfirmer, setAConfirmer] = useState(null);
  const [confirmationEnCours, setConfirmationEnCours] = useState(false);

  const executerConfirmation = async () => {
    if (!aConfirmer?.executer) return;
    setConfirmationEnCours(true);
    try {
      await aConfirmer.executer();
    } finally {
      setConfirmationEnCours(false);
      setAConfirmer(null);
    }
  };

  const navigate = useNavigate();

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      company_name: '',
      email: '',
      phone: '',
      contact_name: '',
      address: '',
      communication_language: 'fr',
      currency: 'EUR',
    }
  });

  const loadSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: parPage,
        search: searchTerm,
      };
      
      const data = await supplierService.getSuppliers(params);
      setSuppliers(data.suppliers);
      setTotalPages(data.pages);
      setTotal(data.total ?? 0);
    } catch (err) {
      // 🔴 ON STOCKE LA CLÉ, ON TRADUIT AU RENDU. `setError(t(…))` fige la
      // traduction À L'INSTANT DE L'ERREUR — et `translations` démarre à `{}`,
      // rempli par un `fetch` asynchrone. Un serveur arrêté fait échouer l'appel
      // API AVANT que le dictionnaire ait répondu : `t()` rend alors la clé
      // brute, et cette clé reste gelée dans l'état, lisible par l'utilisateur
      // (mesuré : « messages.supplierLoadError » à l'écran). Traduire au rendu
      // laisse le message se résoudre dès que le dictionnaire arrive.
      // ⚠️ La clé, elle, existait dans les trois langues : le défaut n'était pas
      // une traduction manquante mais un MOMENT. *Une chaîne traduite rangée
      // dans un état est une traduction datée.*
      setError('messages.supplierLoadError');
      showSnackbar(t('messages.supplierLoadError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, parPage, searchTerm]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  // Recharger les données quand le stock change
  useEffect(() => {
    if (currentStock?.id) {
      loadSuppliers();
    }
  }, [currentStock?.id, loadSuppliers]);

  const handleOpenDialog = (supplier = null) => {
    setEditingSupplier(supplier);
    if (supplier) {
      reset(supplier);
    } else {
      reset({
        company_name: '',
        email: '',
        phone: '',
        contact_name: '',
        address: '',
        communication_language: 'fr',
        currency: 'EUR',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSupplier(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      if (editingSupplier) {
        await supplierService.updateSupplier(editingSupplier.id, data);
        showSnackbar('Fournisseur mis à jour avec succès', 'success');
      } else {
        await supplierService.createSupplier(data);
        showSnackbar('Fournisseur créé avec succès', 'success');
      }
      handleCloseDialog();
      loadSuppliers();
      triggerRefresh(); // Rafraîchir le dashboard
    } catch (err) {
      showSnackbar(err.response?.data?.error || 'Erreur lors de l\'opération', 'error');
    }
  };

  const handleDelete = (supplier) => setAConfirmer({
    nom: supplier.company_name,
    // 🔴 LE SERVEUR REFUSE si le fournisseur porte des PRODUITS ou des
    // COMMANDES — mesuré sur la route. L'annonce le dit AVANT le clic.
    // ⚠️ **MAIS IL NE VÉRIFIE PAS LES ENTRÉES DE STOCK** : c'est la dette
    // « suppression d'un fournisseur orphelinant ses entrées », ancrée au
    // ROADMAP. *On n'annonce que ce qui est servi* — l'avertissement dit donc
    // ce qui se DÉTACHE, sans prétendre que le serveur l'empêche.
    consequences: t('suppliers.deleteConsequence'),
    avertissement: t('suppliers.deleteWarning'),
    executer: () => confirmDelete(supplier.id),
  });

  const confirmDelete = async (supplierId) => {
    try {
      await supplierService.deleteSupplier(supplierId);
      showSnackbar(t('messages.supplierDeleted'), 'success');
      loadSuppliers();
      triggerRefresh(); // Rafraîchir le dashboard
    } catch (err) {
      showSnackbar(err.response?.data?.error || t('messages.deleteError'), 'error');
    }
  };

  if (loading && (!suppliers || suppliers.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  /**
   * 🔴 TOUT FILTRE REMET À LA PAGE 1, DANS LE MÊME GESTE QUE LE FILTRE.
   *
   * Sans ça, un lecteur en page 3 qui tape une recherche demande la page 3 d'un
   * résultat qui n'en a qu'une : **la liste revient vide, et rien ne dit
   * pourquoi**. Mesuré sur `PurchaseOrders` au lot P″ — effacer la recherche ne
   * débloquait même pas, il fallait cliquer la page 1 à la main.
   *
   * ⚠️ DANS LE GESTIONNAIRE, PAS DANS UN EFFET : un `useEffect` corrigerait
   * APRÈS coup, donc après une requête partie avec l'ancienne page. *Un état
   * dérivé d'un autre ne se corrige pas après coup, il s'écrit dans le même
   * geste.*
   */
  const filtrer = (appliquer) => (valeur) => {
    appliquer(valeur);
    setPage(1);
  };

  return (
    <Box sx={{ ml: 0, mr: 0, pl: 0, pr: 0 }}>
      <EnteteEcran titre={t('suppliers.title')} compteur={total || undefined}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          {t('suppliers.newSupplier')}
        </Button>
      </EnteteEcran>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {/* La traduction se fait ICI, au rendu — l'état ne porte qu'une clé. */}
          {t(error)}
        </Alert>
      )}

      {/* Aucun filtre ici : cet écran n'en a qu'un, la recherche. Il passe
          donc `children` vide — il n'a pas besoin d'une prop pour ne pas avoir
          de filtre. */}
      <BarreFiltres
        valeur={searchTerm}
        onChange={filtrer(setSearchTerm)}
        libelle={t('common.search')}
      />

      {/* Liste des fournisseurs */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          ml: 0, 
          mr: 0,
          '& .MuiPaper-root': {
            padding: 0,
            margin: 0,
          }
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {/* 🔴 TROIS COLONNES DE CONTACT DEVIENNENT UNE. Contact, e-mail et
                  téléphone décrivent la MÊME personne à joindre ; en colonnes
                  séparées, chacune prenait une largeur pour une valeur qu'on ne
                  compare jamais d'une ligne à l'autre. Empilées, elles se lisent
                  comme une fiche. Même geste qu'au tableau des commandes, où la
                  date a rejoint le numéro. */}
              <TableCell>{t('suppliers.companyName')}</TableCell>
              <TableCell>{t('suppliers.contact')}</TableCell>
              <TableCell>{t('suppliers.address')}</TableCell>
              <TableCell align="right">{t('suppliers.activity')}</TableCell>
              <TableCell align="right">{t('products.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(suppliers || []).map((supplier) => (
              /* 🔴 LA LIGNE MÈNE AU DÉTAIL, PLUS AU FORMULAIRE — c'est le point 4
                 de RU5-d, et il ferme la dernière divergence de l'application :
                 sur Produits et Commandes, cliquer une ligne OUVRE, cliquer le
                 crayon MODIFIE. Ici la ligne ouvrait directement la saisie, donc
                 consulter un fournisseur demandait d'entrer dans son formulaire.
                 *Un écran qui n'a que le mode édition force à risquer une
                 modification pour lire.* */
              <TableRow key={supplier.id} hover style={{ cursor: 'pointer' }} onClick={e => {
                // Ne pas ouvrir si clic sur une icône d'action
                if (e.target.closest('button')) return;
                navigate(`/suppliers/${supplier.id}`);
              }}>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {supplier.company_name}
                  </Typography>
                  {/* 🔴 LA DEVISE ÉTAIT INVISIBLE DANS TOUTE L'APPLICATION. Elle est
                      portée par le fournisseur, elle décide de l'affichage de TOUS
                      les montants de ses commandes ET du groupement des totaux de
                      la Comptabilité — et on ne pouvait la lire nulle part. *Un
                      champ qui décide d'un écran sans être montré ne peut pas être
                      vérifié.* Elle était déjà servie par `to_dict()` : zéro
                      backend, il suffisait de la rendre. */}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {supplier.currency || 'EUR'} · {getCurrencySymbol(supplier.currency || 'EUR')}
                  </Typography>
                </TableCell>
                <TableCell>
                  {/* Les trois lignes de contact empilées : un nom, un e-mail, un
                      téléphone se lisent comme une fiche, pas comme trois mesures
                      à comparer. Les absences ne sont PAS remplies de « non
                      renseigné » trois fois — une fiche vide se voit mieux qu'une
                      fiche de trois mentions d'absence. */}
                  <Typography variant="body2">
                    {supplier.contact_name || t('suppliers.notSpecified')}
                  </Typography>
                  {supplier.email && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {supplier.email}
                    </Typography>
                  )}
                  {supplier.phone && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {supplier.phone}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {supplier.address || t('suppliers.notSpecified')}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {/* 🔴 CE QUE CE FOURNISSEUR PORTE — et ce qu'on perdrait à le
                      supprimer. ⚠️ LE COMPTEUR REND LA CONSÉQUENCE VISIBLE, IL NE
                      L'EMPÊCHE PAS : voir « 12 produits » n'est pas la même chose
                      qu'être refusé. La dette « supprimer un fournisseur orpheline
                      ses entrées de stock » reste OUVERTE — un affichage n'est pas
                      une garde. */}
                  <Typography variant="body2">
                    {t('suppliers.productsCount', { count: supplier.products_count ?? 0 })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('suppliers.ordersCount', { count: supplier.orders_count ?? 0 })}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {/* Les deux gestes sont désormais DISTINCTS et tous deux
                      visibles : l'œil consulte, le crayon modifie. La ligne
                      entière double le premier — jamais le second. */}
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/suppliers/${supplier.id}`)}
                  >
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(supplier)}
                  >
                    <EditIcon />
                  </IconButton>
                  {canDelete(user, DELETABLE.SUPPLIERS) && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(supplier)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DialogueConfirmation
        ouvert={Boolean(aConfirmer)}
        titre={t('suppliers.deleteTitle')}
        question={t('suppliers.deleteQuestion', { nom: aConfirmer?.nom || '' })}
        consequences={aConfirmer?.consequences}
        avertissement={aConfirmer?.avertissement}
        libelleConfirmer={t('common.delete')}
        enCours={confirmationEnCours}
        onConfirmer={executerConfirmation}
        onFermer={() => setAConfirmer(null)}
      />

      <PiedTableau
        total={total}
        page={page}
        pages={totalPages}
        parPage={parPage}
        onPage={setPage}
        onParPage={(n) => { setParPage(n); setPage(1); }}
        libelleTotal={`${total} ${t('suppliers.title').toLowerCase()}`}
        libelleParPage={t('common.perPage')}
      />

      {/* Dialog pour créer/modifier un fournisseur */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSupplier ? t('suppliers.editSupplier') : t('suppliers.newSupplier')}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="company_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('suppliers.companyName')}
                      error={!!errors.company_name}
                      helperText={errors.company_name?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="contact_name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('suppliers.contactName')}
                      error={!!errors.contact_name}
                      helperText={errors.contact_name?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('suppliers.email')}
                      type="email"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('suppliers.phone')}
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('suppliers.address')}
                      multiline
                      rows={3}
                      error={!!errors.address}
                      helperText={errors.address?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="communication_language"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label={t('suppliers.communicationLanguage')}
                      error={!!errors.communication_language}
                      helperText={errors.communication_language?.message}
                    >
                      <MenuItem value="fr">{t('suppliers.languageFrench')}</MenuItem>
                      <MenuItem value="en">{t('suppliers.languageEnglish')}</MenuItem>
                      <MenuItem value="he">{t('suppliers.languageHebrew')}</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label={t('suppliers.currency')}
                      error={!!errors.currency}
                      helperText={errors.currency?.message}
                    >
                      <MenuItem value="EUR">EUR (€)</MenuItem>
                      <MenuItem value="ILS">ILS (₪)</MenuItem>
                      <MenuItem value="USD">USD ($)</MenuItem>
                      <MenuItem value="GBP">GBP (£)</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
            </Grid>

            <Box display="flex" gap={2} justifyContent="flex-end" mt={3}>
              <Button
                variant="outlined"
                onClick={handleCloseDialog}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
              >
                {editingSupplier ? t('common.edit') : t('common.add')}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        {/* 🔴 LA CARTE « DERNIÈRES COMMANDES » A QUITTÉ CE DIALOGUE. Elle était
            ici un frère de `DialogContent`, pas un enfant — donc HORS de la zone
            de défilement du dialogue. Le correctif n'est pas de la réimbriquer :
            c'est de la mettre là où elle appartient, dans le détail en lecture.
            *Une imbrication fausse est souvent le symptôme d'un contenu qui
            n'est pas à sa place.*
            ⚠️ Elle affichait aussi les CINQ PREMIÈRES commandes sous le titre
            « dernières » — aucun `order_by` nulle part, un `.slice(0, 5)` pour
            tout tri. C'est corrigé côté serveur, où l'ordre survit à la
            pagination. */}
      </Dialog>

      {/* ⚠️ `key` SUR L'IDENTIFIANT : passer d'un fournisseur à l'autre sans
          fermer (c'est possible en changeant l'URL) doit REMONTER le composant,
          sinon l'onglet, la page et l'ancien fournisseur survivraient à la
          bascule. */}
      <DialogueDetailFournisseur
        key={fournisseurDetailleId}
        id={fournisseurDetailleId}
        ouvert={Boolean(fournisseurDetailleId)}
        onFermer={() => navigate('/suppliers')}
        onModifier={async () => {
          // Le détail SE FERME et le formulaire s'ouvre — on n'empile pas deux
          // dialogues. Le fournisseur est RELU : le tableau derrière peut être
          // périmé, et éditer une copie d'affichage est la péremption d'état
          // qui nous a déjà coûté un lot.
          navigate('/suppliers');
          try {
            const frais = await supplierService.getSupplier(fournisseurDetailleId);
            handleOpenDialog(frais.supplier || frais);
          } catch (e) {
            showSnackbar(t('suppliers.notFound'), 'error');
          }
        }}
      />
    </Box>
  );
};

export default Suppliers; 